import * as pdfjsLib from 'pdfjs-dist';
import { RosterDTO, RosterImportStrategy, RosterPlayerDTO } from './types';

// Dynamically resolve worker source matching the exact npm version of pdfjs-dist at runtime
const pdfjsVersion = pdfjsLib.version;
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/build/pdf.worker.mjs`;


export class TourPlayPdfParser implements RosterImportStrategy {
  public id = 'tourplay-pdf';
  public name = 'TourPlay.net PDF Roster';
  public description = 'Upload the printed/PDF roster sheet generated from TourPlay.net';
  public acceptFileType = '.pdf';

  public async parse(file: File): Promise<RosterDTO> {
    const arrayBuffer = await this.readFileAsArrayBuffer(file);
    const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) });
    const pdf = await loadingTask.promise;

    if (pdf.numPages < 1) {
      throw new Error('The uploaded PDF document has no pages.');
    }

    const page = await pdf.getPage(1);
    const content = await page.getTextContent();
    
    const rawItems = content.items
      .map((item: any) => {
        const x = item.transform ? Math.round(item.transform[4]) : 0;
        const y = item.transform ? Math.round(item.transform[5]) : 0;
        return { str: item.str.trim(), x, y };
      })
      .filter(item => item.str.length > 0);

    // 1. Deduplicate bold text shadow items
    const items: { str: string; x: number; y: number }[] = [];
    rawItems.forEach(item => {
      const isDuplicate = items.some(existing => 
        existing.str === item.str && 
        Math.abs(existing.x - item.x) <= 3 && 
        Math.abs(existing.y - item.y) <= 3
      );
      if (!isDuplicate) {
        items.push(item);
      }
    });

    // 2. Parse Team Name & Race
    const teamNameItem = items.find(item => Math.abs(item.y - 569) <= 3);
    const raceItem = items.find(item => Math.abs(item.y - 559) <= 3 && item.str !== '1,475' && item.str !== 'CTV');

    const teamName = teamNameItem ? teamNameItem.str : 'Imported Team';
    let race = raceItem ? raceItem.str : 'Imperial Nobility';
    if (race === 'IMPERIAL NOBILITY') {
      race = 'Imperial Nobility';
    }

    // 3. Parse Treasury & Staff
    // NOTE: Treasury value and staff counts are rendered as graphical overlays and cannot
    // be read by pdfjs. However, we can detect WHICH staff are present by their labels.
    const treasury = undefined;

    // Detect staff presence by label text
    const hasRerolls = items.some(item => item.str.toLowerCase().includes('re-roll'));
    const hasAssistantCoaches = items.some(item => item.str.toLowerCase().includes('assistant coach'));
    const hasApothecary = items.some(item => item.str.toLowerCase().includes('apothecary'));
    const hasDedicatedFans = items.some(item => item.str.toLowerCase().includes('dedicated fan'));

    const rerollsCount = hasRerolls ? 0 : undefined;
    const assistantCoaches = hasAssistantCoaches ? 0 : undefined;
    const apothecary = hasApothecary ? 0 : undefined;
    const dedicatedFans = hasDedicatedFans ? 0 : undefined;

    // 4. Find Player baseline Ys
    const playerNumberItems = items.filter(item => /^#\d+$/.test(item.str));
    const playerBaselines = playerNumberItems.map(item => item.y).sort((a, b) => b - a);

    const players: RosterPlayerDTO[] = [];
    const labelsToExclude = new Set(['MA', 'ST', 'AG', 'PA', 'AV', 'AV+', 'AR', 'No.', 'PLAYER', 'CHARACTERISTICS', 'SKILLS', 'AND', 'TRAITS', 'VALUE']);

    for (const baseY of playerBaselines) {
      const bandItems = items.filter(item => Math.abs(item.y - baseY) <= 8);
      
      const numItem = bandItems.find(item => /^#\d+$/.test(item.str));
      if (!numItem) continue;
      const number = parseInt(numItem.str.slice(1), 10);

      // Sort left column items (X < 200) by Y descending to parse the vertical stack
      const leftColumn = bandItems
        .filter(item => item.x < 200)
        .sort((a, b) => b.y - a.y);

      const numberIdx = leftColumn.findIndex(item => item.str === numItem.str);
      
      let name = 'Unknown Name';
      let position = 'Unknown Position';
      let keywords = '';

      if (numberIdx > 0) {
        name = leftColumn[numberIdx - 1].str;
      }
      if (numberIdx !== -1 && numberIdx + 1 < leftColumn.length) {
        position = leftColumn[numberIdx + 1].str;
        if (numberIdx + 2 < leftColumn.length && leftColumn[numberIdx + 2].str.startsWith('(')) {
          keywords = leftColumn[numberIdx + 2].str;
        }
      }

      // Parse Stats (200 <= X < 310)
      const statItems = bandItems
        .filter(item => item.x >= 200 && item.x < 310 && !labelsToExclude.has(item.str))
        .sort((a, b) => a.x - b.x);

      const statValues: string[] = [];
      let currentStat = '';
      statItems.forEach((item, index) => {
        currentStat += item.str;
        const nextItem = statItems[index + 1];
        if (!nextItem || nextItem.x - item.x > 8) {
          statValues.push(currentStat);
          currentStat = '';
        }
      });

      const statsObj = {
        MA: statValues[0] || '5',
        ST: statValues[1] || '3',
        AG: statValues[2] || '3+',
        PA: statValues[3] || '4+',
        AR: statValues[4] || '8+',
        Skills: '',
        Primary: '',
        Secondary: '',
        Cost: '0'
      };

      // Parse Skills (310 <= X < 630)
      const skillItems = bandItems
        .filter(item => item.x >= 310 && item.x < 630 && !labelsToExclude.has(item.str))
        .sort((a, b) => a.x - b.x);

      // Clean skill names by stripping trailing dot indicators
      const rawSkillsText = skillItems.map(item => item.str).join(' ');
      const skillsList = rawSkillsText
        .split(',')
        .map(s => s.replace(/·/g, '').trim())
        .filter(s => s.length > 0 && s !== '·');

      statsObj.Skills = skillsList.join(', ');

      // Parse SPP and Level (650 <= X < 685)
      const sppItems = bandItems.filter(item => item.x >= 650 && item.x < 685);
      let spp = 0;
      let level = 'Rookie';
      sppItems.forEach(item => {
        if (item.str.includes('★') || item.str.includes('/') || /^\d+$/.test(item.str)) {
          const val = parseInt(item.str.replace(/[^0-9]/g, ''), 10);
          if (!isNaN(val) && item.str.includes('/')) {
            spp = parseInt(item.str.split('/')[0].replace(/[^0-9]/g, ''), 10) || 0;
          } else if (!isNaN(val) && !item.str.includes('/')) {
            spp = val;
          }
        } else if (['Rookie', 'Experienced', 'Veteran', 'Star', 'Super Star', 'Legend'].includes(item.str)) {
          level = item.str;
        }
      });

      // Parse Career Stats (TD, CAS, COMP, INT, MVP) by coordinate columns
      const getCareerVal = (startX: number, endX: number) => {
        const match = bandItems.find(item => item.x >= startX && item.x < endX && item.str !== '·' && !labelsToExclude.has(item.str));
        return match ? parseInt(match.str, 10) || 0 : 0;
      };

      const careerStats = {
        TD: getCareerVal(685, 710),
        CAS: getCareerVal(710, 735),
        COMP: getCareerVal(735, 760),
        INT: getCareerVal(760, 780),
        MVP: getCareerVal(780, 805)
      };

      // Parse Cost (X >= 805)
      const valItems = bandItems.filter(item => item.x >= 805);
      const valueStr = valItems.find(item => /^\d{2,3},\d{3}$/.test(item.str))?.str || '0';
      const costValue = parseInt(valueStr.replace(/,/g, ''), 10) || 0;
      statsObj.Cost = costValue.toLocaleString() + ' GP';

      players.push({
        number,
        name,
        position,
        keywords,
        skills: skillsList,
        stats: statsObj,
        spp,
        level,
        careerStats
      });
    }

    return {
      teamName,
      race,
      treasury,
      staffCounts: {
        rerolls: rerollsCount,
        assistantCoaches,
        apothecary,
        dedicatedFans
      },
      players: players.sort((a, b) => a.number - b.number)
    };
  }

  private readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as ArrayBuffer);
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(file);
    });
  }
}
